import { apiClient } from '@/lib/api/client'

// ─── Types ──────────────────────────────────────────────
//
// Mirrors lib/growth/report.js. Campaign numbers are live from Google Ads (cached 10 min);
// search terms and products are from the nightly sync. Money is in the account's currency.

export const WINDOWS = [7, 14, 30, 90] as const
export type WindowDays = (typeof WINDOWS)[number]

export interface Sync {
  status: 'running' | 'ok' | 'failed'
  window_to: string
  finished_at: string | null
  error: string | null
}

export interface Account {
  customerId: string
  name: string
  currency: string
  timeZone: string
  market: string
  destination: string | null
  sync?: Sync | null
}

export interface Metrics {
  cost: number
  clicks: number
  impressions: number
  purchases: number
  value: number
  ctr: number
  cpc: number
  cvr: number
  cpa: number | null
  roas: number
}

export interface DayMetrics extends Metrics { date: string }

export interface Share {
  impressionShare: number | null
  lostToRank: number | null
  lostToBudget: number | null
}

export interface Campaign {
  id: string
  name: string
  status: string
  channel: string
  bidding: string
  budget: number
  cur: Metrics
  prev: Metrics
  today: Metrics
  share: Share
  series: DayMetrics[]
}

export interface Window {
  days: number
  from: string
  to: string
  prevFrom: string
  prevTo: string
  today: string
}

export interface StoreTotals { orders: number; revenue: number; adCheckouts: number }

export interface Overview {
  account: Account
  window: Window
  breakEvenRoas: number
  campaigns: Campaign[]
  totals: { budget: number; cur: Metrics; prev: Metrics; today: Metrics }
  series: DayMetrics[]
  store: {
    cur: StoreTotals
    prev: StoreTotals
    series: Array<{ date: string; orders: number; revenue: number; adCheckouts: number }>
    blended: { cur: { mer: number | null; costPerOrder: number | null }; prev: { mer: number | null; costPerOrder: number | null } }
  }
  sync: Sync | null
  readAt: string
}

export interface ProductGroup {
  adGroupId: string
  criterionId: string
  dimension: string | null
  value: string | null
  excluded: boolean
  bid: number | null
  metrics: Metrics
}

export interface CampaignDetail {
  account: Account
  window: Window
  breakEvenRoas: number
  campaign: Campaign | null
  adGroups: Array<{ id: string; name: string; status: string; bid: number }>
  productGroups: ProductGroup[]
  sync: Sync | null
  readAt: string
}

export interface TermRow {
  term: string
  class: string
  room: string | null
  campaigns: string[]
  impressions: number
  clicks: number
  cost: number
  purchases: number
  value: number
  cpc: number | null
  proposal: string | null
}

export interface ClassRow { class: string; terms: number; clicks: number; cost: number; purchases: number }

export interface ProductRow {
  item_id: string
  impressions: number
  clicks: number
  cost: number
  purchases: number
  value: number
  cpc: number | null
  title: string | null
  image: string | null
  link: string | null
  kind: 'individual' | 'set'
  artworkId: number | null
  room: string | null
  tier: 'hero' | 'core' | 'tail' | null
  inFeed: boolean
  eligible: boolean | null
}

export type PlanState = 'live' | 'ready' | 'waiting' | 'blocked' | 'planned'
export interface PlanStep { key: string; title: string; state: PlanState; reading: string; next?: string }

export interface Strategy {
  breakEven: number
  plan: PlanStep[]
  errors: string[]
  tracking?: {
    orders: Array<{ status: string; reason: string; n: number; with_visitor: number }>
    checkouts: { sent: number; failed: number; with_visitor: number }
  }
  intent?: {
    mode: string
    scores: number
    scoresToday: number
    lastReceived: string | null
    scoredConversions30d: number
    qualifiedToday: number
    qualified30d: number
  }
  mining?: {
    runs: Array<{ customer_id: string; status: string; started_at: string; error: string | null }>
    applied: Array<{ customer_id: string; n: number }>
    waiting: Array<{ customer_id: string; kind: string; n: number }>
    wasted: Array<{ customer_id: string; term: string; class: string; cost: number; clicks: number }>
  }
  tiers?: Array<{ destination: string; hero: number; core: number; promoted: number; demoted: number; held: number }>
  labels?: Array<{
    customerId: string
    destination: string | null
    labels: Array<{ label: string; meaning: string; feedItems: number; google?: boolean; missing: boolean }>
    products?: Record<string, number>
  }>
  tiersCutover: string
  readAt: string
}

export interface ListParams {
  days: number
  campaign?: string
  q?: string
  class?: string
  sort?: string
  limit?: number
  noSale?: boolean
}

// ─── Client ─────────────────────────────────────────────

const BASE = '/api/growth-admin'

export const growthApi = {
  getAccounts: async (): Promise<{ data: Account[] }> => (await apiClient.get(`${BASE}/accounts`)).data,

  getOverview: async (customerId: string, days: number): Promise<Overview> =>
    (await apiClient.get(`${BASE}/accounts/${customerId}/overview`, { params: { days } })).data,

  getCampaign: async (customerId: string, campaignId: string, days: number): Promise<CampaignDetail> =>
    (await apiClient.get(`${BASE}/accounts/${customerId}/campaigns/${campaignId}`, { params: { days } })).data,

  getSearchTerms: async (customerId: string, params: ListParams): Promise<{ account: Account; window: Window; data: TermRow[]; classes: ClassRow[]; sync: Sync | null }> =>
    (await apiClient.get(`${BASE}/accounts/${customerId}/search-terms`, { params })).data,

  getProducts: async (customerId: string, params: ListParams): Promise<{ account: Account; window: Window; data: ProductRow[]; totals: { items: number; impressions: number }; sync: Sync | null }> =>
    (await apiClient.get(`${BASE}/accounts/${customerId}/products`, { params })).data,

  getStrategy: async (): Promise<Strategy> => (await apiClient.get(`${BASE}/strategy`)).data,
}
